import { motion, useReducedMotion } from 'motion/react'

import { ButtonLink } from '@/components/ButtonLink'
import { DonationPanel } from '@/components/DonationPanel'
import { Marquee } from '@/components/Marquee'
import { ResourceCard } from '@/components/ResourceCard'
import { Reveal } from '@/components/Reveal'
import { SectionIntro } from '@/components/SectionIntro'
import { impactItems, resourceItems } from '@/data/site'

export function HomePage() {
  const reduceMotion = useReducedMotion()

  return (
    <>
      <section className="hero">
        <motion.div
          className="hero-media"
          initial={reduceMotion ? false : { opacity: 0, scale: 1.06 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
        >
          <img
            src="/assets/images/noa-hero.webp"
            alt="Noa recostada mirando a cámara"
            width="1920"
            height="1081"
            fetchPriority="high"
          />
        </motion.div>
        <div className="hero-overlay" aria-hidden="true" />

        <div className="container-wide hero-grid">
          <div className="hero-copy">
            <motion.p
              className="eyebrow"
              initial={reduceMotion ? false : { opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              Recursos · apoyo · comunidad
            </motion.p>
            <motion.h1
              className="display-title"
              initial={reduceMotion ? false : { opacity: 0, y: 36 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.85, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
            >
              <span className="hero-title-line">La esperanza</span>
              {' '}
              <span className="hero-title-line hero-title-accent">
                es obligatoria.
              </span>
            </motion.h1>
            <div className="hero-support">
              <motion.p
                className="lede"
                initial={reduceMotion ? false : { opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.75, delay: 0.2 }}
              >
                Acompañamos a familias con niñas y niños que viven una lesión cerebral
                para que encuentren herramientas y comunidad en el momento que más las
                necesitan.
              </motion.p>
              <div className="hero-action-cluster">
                <motion.p
                  className="hero-promise"
                  initial={reduceMotion ? false : { opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.65, delay: 0.26 }}
                >
                  <span>Un movimiento que abraza</span>
                  Cada familia merece información, escucha y esperanza.
                </motion.p>
                <motion.div
                  className="hero-actions"
                  initial={reduceMotion ? false : { opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.34 }}
                >
                  <ButtonLink href="/nuestra-historia/">Conoce la historia</ButtonLink>
                  <ButtonLink href="/recursos/" variant="light">
                    Explora recursos
                  </ButtonLink>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Marquee />

      <section className="section-space" aria-labelledby="mission-title">
        <div className="container-wide">
          <div className="mission-grid">
            <Reveal>
              <p className="eyebrow">Nuestra misión</p>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="mission-statement" id="mission-title">
                Convertir la incertidumbre en <em>posibilidades compartidas.</em>
              </h2>
              <p className="lede mt-8">
                Acercamos aprendizajes, apoyo y conexiones para que cada familia pueda
                avanzar mejor acompañada y tomar decisiones con más claridad.
              </p>
            </Reveal>
          </div>

          <div className="impact-grid">
            {impactItems.map((item, index) => {
              const Icon = item.icon

              return (
                <Reveal delay={index * 0.1} key={item.title}>
                  <article className="impact-card">
                    <span className="impact-index">0{index + 1}</span>
                    <span className="impact-icon">
                      <Icon aria-hidden="true" size={34} strokeWidth={1.5} />
                    </span>
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                  </article>
                </Reveal>
              )
            })}
          </div>
        </div>
      </section>

      <section className="story-preview section-space" aria-labelledby="story-preview-title">
        <div className="container-wide story-grid">
          <Reveal className="photo-collage">
            <img
              src="/assets/images/noa-mama.webp"
              alt="Noa compartiendo un momento cercano con su mamá"
              width="1200"
              height="1600"
              loading="lazy"
            />
            <img
              src="/assets/images/noa-playa.webp"
              alt="Noa jugando con arena en la playa"
              width="1200"
              height="1600"
              loading="lazy"
            />
          </Reveal>

          <Reveal delay={0.12}>
            <p className="eyebrow !text-apricot before:!bg-apricot">Nuestra historia</p>
            <h2 className="section-title" id="story-preview-title">
              Una niña. Una familia. Una red que eligió no rendirse.
            </h2>
            <p className="lede mt-7 !text-white/70">
              El camino de Noa reveló lo difícil que puede ser encontrar respuestas y
              lo poderoso que resulta encontrarse con otras personas dispuestas a
              ayudar.
            </p>
            <div className="mt-8">
              <ButtonLink href="/nuestra-historia/" variant="primary">
                Leer nuestra historia
              </ButtonLink>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="section-space" aria-labelledby="resources-title">
        <div className="container-wide">
          <SectionIntro
            eyebrow="Biblioteca viva"
            id="resources-title"
            title="Información que sí puedes encontrar."
            description="Un punto de partida humano para explorar opciones, preparar preguntas y descubrir redes de apoyo."
          />
          <div className="resource-grid">
            {resourceItems.map((item, index) => (
              <ResourceCard item={item} index={index} key={item.title} />
            ))}
          </div>
          <Reveal className="mt-8 flex justify-center">
            <ButtonLink href="/recursos/" variant="dark">
              Ver todos los recursos
            </ButtonLink>
          </Reveal>
        </div>
      </section>

      <DonationPanel />
    </>
  )
}
